import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, X, Upload, Instagram, Banknote, CheckCircle2, AlertCircle, Loader2, ExternalLink, Truck } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SellerOnboarding, StripeWarningBanner } from "@/components/SellerOnboarding";
import { toast } from "sonner";

const resolveImg = (img) => img?.startsWith("http") ? img : `${API}/files/${img}`;

const SellerOrderFulfillment = ({ order, onSaved }) => {
  const [f, setF] = useState({
    fulfillment_status: order.fulfillment_status || "unfulfilled",
    tracking_carrier: order.tracking_carrier || "",
    tracking_number: order.tracking_number || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/orders/${order.id}/fulfillment`, f); toast.success("Fulfillment updated"); onSaved && onSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 border-t border-border/40 pt-4" data-testid={`seller-fulfillment-${order.id.slice(0, 8)}`}>
      <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3 flex items-center gap-1.5"><Truck className="w-3 h-3" /> Fulfillment & tracking</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <select data-testid="seller-fulfillment-status" value={f.fulfillment_status}
          onChange={(e) => setF({ ...f, fulfillment_status: e.target.value })}
          className="bg-transparent border-b border-border focus:border-primary outline-none py-2 text-sm">
          <option value="unfulfilled">Unfulfilled</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
        </select>
        <input data-testid="seller-tracking-carrier" value={f.tracking_carrier} placeholder="Carrier (e.g. Canada Post)"
          onChange={(e) => setF({ ...f, tracking_carrier: e.target.value })}
          className="bg-transparent border-b border-border focus:border-primary outline-none py-2 text-sm" />
        <input data-testid="seller-tracking-number" value={f.tracking_number} placeholder="Tracking number"
          onChange={(e) => setF({ ...f, tracking_number: e.target.value })}
          className="bg-transparent border-b border-border focus:border-primary outline-none py-2 text-sm" />
      </div>
      <button onClick={save} disabled={saving} data-testid="seller-save-fulfillment"
        className="btn-primary mt-3 px-4 py-2 text-xs rounded-sm inline-flex items-center gap-2">
        {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
      </button>
    </div>
  );
};
const CATEGORIES = ["Candles", "Skincare", "Bath & Body", "Beauty", "Jewelry", "Gift Boxes", "Metaphysical & Mystical"];
const EMPTY = { title: "", description: "", price: "", category: "", stock: 1, images: [], tags: [], options: [], variants: [], shipping_canada: "", shipping_us: "", shipping_international: "", free_shipping: false };
const regionNames = (typeof Intl !== "undefined" && Intl.DisplayNames) ? new Intl.DisplayNames(["en"], { type: "region" }) : null;
const countryName = (code) => { try { return regionNames ? regionNames.of(code) : code; } catch { return code; } };

export default function SellerDashboard() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState({
    name: "", bio: "", instagram: "", business_name: "", instagram_posts_text: "", logo: "", banner: "",
    shipping_domestic: "", shipping_international: "", ships_internationally: false, free_shipping_threshold: "",
  });
  const [uploadingProfile, setUploadingProfile] = useState(null);
  const [payout, setPayout] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [changeCountry, setChangeCountry] = useState(false);
  const tabsRef = useRef(null);

  const goToField = (tab, anchor) => {
    setTab(tab);
    setTimeout(() => {
      const el = (anchor && document.getElementById(anchor)) || tabsRef.current;
      el?.scrollIntoView({ behavior: "smooth", block: anchor ? "center" : "start" });
    }, 90);
  };

  const fetchPayoutStatus = async () => {
    try {
      const { data } = await api.get("/seller/stripe/status");
      setPayout(data);
    } catch {
      setPayout({ connected: false });
    }
  };

  const connectStripe = async (country) => {
    setConnecting(true);
    try {
      const body = { origin_url: window.location.origin };
      if (country) body.country = country;
      const { data } = await api.post("/seller/stripe/onboard", body);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setConnecting(false);
    }
  };

  const load = async () => {
    if (!user) return;
    const isAdmin = user.role === "admin";
    const [{ data: ps }, { data: os }] = await Promise.all([
      api.get(isAdmin ? `/products?limit=500` : `/products?seller_id=${user.id}`),
      api.get("/orders/seller").catch(() => ({ data: [] })),
    ]);
    setProducts(ps);
    setOrders(os);
    setProfile({
      name: user.name || "",
      bio: user.bio || "",
      logo: user.logo || "",
      banner: user.banner || "",
      instagram: user.instagram || "",
      business_name: user.business_name || "",
      instagram_posts_text: (user.instagram_posts || []).join("\n"),
      shipping_domestic: user.shipping_domestic ? String(user.shipping_domestic) : "",
      shipping_international: user.shipping_international ? String(user.shipping_international) : "",
      ships_internationally: !!user.ships_internationally,
      free_shipping_threshold: user.free_shipping_threshold ? String(user.free_shipping_threshold) : "",
    });
  };
  useEffect(() => { load(); }, [user]);
  // Refresh user once on mount so Stripe status is current (e.g. after returning from Stripe)
  useEffect(() => { refresh?.(); }, []);

  // Deep-link: /seller?new=1 opens the new-listing form, /seller?tab=profile selects Profile tab
  useEffect(() => {
    if (params.get("new") === "1") {
      setEditing("new"); setForm(EMPTY);
      const p = new URLSearchParams(params); p.delete("new"); setParams(p, { replace: true });
    }
    const t = params.get("tab");
    if (t === "profile" || t === "orders" || t === "products" || t === "payouts") {
      setTab(t);
      const p = new URLSearchParams(params); p.delete("tab"); setParams(p, { replace: true });
    }
    if (params.get("connected") === "1" || params.get("refresh") === "1") {
      setTab("payouts");
      fetchPayoutStatus();
      if (params.get("connected") === "1") toast.success("Returned from Stripe — checking your payout status…");
      const p = new URLSearchParams(params); p.delete("connected"); p.delete("refresh"); setParams(p, { replace: true });
    }
  }, [params]);

  useEffect(() => { if (tab === "payouts" && payout === null) fetchPayoutStatus(); }, [tab]);
  useEffect(() => {
    if (tab === "payouts" && countries.length === 0) {
      api.get("/stripe/countries").then(({ data }) => setCountries(data.countries || [])).catch(() => {});
    }
  }, [tab]);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      price: String(p.price),
      tags: p.tags || [],
      shipping_canada: p.shipping_canada ?? "",
      shipping_us: p.shipping_us ?? "",
      shipping_international: p.shipping_international ?? "",
      free_shipping: !!p.free_shipping,
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const uploadFiles = async (files) => {
    setUploading(true);
    const newPaths = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPaths.push(data.path);
      } catch (err) {
        toast.error("Upload failed: " + formatApiErrorDetail(err.response?.data?.detail));
      }
    }
    setForm((f) => ({ ...f, images: [...(f.images || []), ...newPaths] }));
    setUploading(false);
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  // ---- Product options / variants ----
  const [valueDraft, setValueDraft] = useState({});
  const [applyPrice, setApplyPrice] = useState("");

  const buildVariants = (options, existing) => {
    const valid = options.filter((o) => (o.name || "").trim() && (o.values || []).length);
    if (!valid.length) return [];
    let combos = [{}];
    for (const o of valid) {
      const next = [];
      for (const c of combos) for (const v of o.values) next.push({ ...c, [o.name.trim()]: v });
      combos = next;
      if (combos.length > 300) break;
    }
    const exMap = {};
    (existing || []).forEach((v) => { exMap[v.id] = v; });
    return combos.slice(0, 300).map((optsObj) => {
      const id = valid.map((o) => `${o.name.trim()}:${optsObj[o.name.trim()]}`).join(" / ");
      const prev = exMap[id];
      return prev ? { ...prev, options: optsObj } : { id, options: optsObj, price: "", stock: 0, sku: "", image: "" };
    });
  };
  const setOptions = (newOptions) =>
    setForm((f) => ({ ...f, options: newOptions, variants: buildVariants(newOptions, f.variants) }));
  const addOption = () => setOptions([...(form.options || []), { name: "", values: [] }]);
  const removeOption = (i) => setOptions((form.options || []).filter((_, idx) => idx !== i));
  const setOptName = (i, name) => setOptions((form.options || []).map((o, idx) => (idx === i ? { ...o, name } : o)));
  const addValue = (i) => {
    const val = (valueDraft[i] || "").trim();
    if (!val) return;
    setOptions((form.options || []).map((o, idx) => (idx === i && !o.values.includes(val) ? { ...o, values: [...o.values, val] } : o)));
    setValueDraft((d) => ({ ...d, [i]: "" }));
  };
  const removeValue = (i, val) =>
    setOptions((form.options || []).map((o, idx) => (idx === i ? { ...o, values: o.values.filter((x) => x !== val) } : o)));
  const updateVariant = (id, patch) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v) => (v.id === id ? { ...v, ...patch } : v)) }));
  const applyPriceToAll = () => {
    const price = parseFloat(applyPrice);
    if (!(price > 0)) { toast.error("Enter a valid price to apply to all"); return; }
    setForm((f) => ({ ...f, variants: f.variants.map((v) => ({ ...v, price })) }));
    toast.success("Applied price to all variants");
  };
  const uploadVariantImage = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateVariant(id, { image: data.path });
    } catch (err) {
      toast.error("Upload failed: " + formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const hasVariantMode = () => (form.options || []).some((o) => (o.name || "").trim() && (o.values || []).length);

  const uploadProfileImage = async (file, key) => {
    if (!file) return;
    setUploadingProfile(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, [key]: data.path }));
    } catch (err) {
      toast.error("Upload failed: " + formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingProfile(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error("Please select a category");
      return;
    }
    const variantMode = hasVariantMode();
    let price = parseFloat(form.price);
    let optionsPayload = [];
    let variantsPayload = [];
    if (variantMode) {
      optionsPayload = (form.options || [])
        .filter((o) => (o.name || "").trim() && (o.values || []).length)
        .map((o) => ({ name: o.name.trim(), values: o.values }));
      variantsPayload = (form.variants || []).map((v) => ({
        id: v.id,
        options: v.options,
        price: parseFloat(v.price || 0),
        stock: parseInt(v.stock || 0, 10),
        sku: v.sku || null,
        image: v.image || null,
      }));
      if (!variantsPayload.length || variantsPayload.some((v) => !(v.price > 0))) {
        toast.error("Please set a price greater than 0 for every variant.");
        return;
      }
      price = Math.min(...variantsPayload.map((v) => v.price));
    } else if (!(price > 0)) {
      toast.error("Please enter a price");
      return;
    }
    const payload = {
      title: form.title, description: form.description, price,
      category: form.category, stock: parseInt(form.stock || 1, 10),
      images: form.images, tags: form.tags || [],
      options: optionsPayload, variants: variantsPayload,
      shipping_canada: form.shipping_canada === "" ? null : parseFloat(form.shipping_canada),
      shipping_us: form.shipping_us === "" ? null : parseFloat(form.shipping_us),
      shipping_international: form.shipping_international === "" ? null : parseFloat(form.shipping_international),
      free_shipping: !!form.free_shipping,
    };
    try {
      let pid = editing;
      if (editing === "new") {
        const { data } = await api.post("/products", payload);
        pid = data.id;
        toast.success("Listing created");
      } else {
        await api.put(`/products/${editing}`, payload);
        toast.success("Listing updated");
      }
      if (user?.role === "admin" && pid) {
        await api.put(`/admin/products/${pid}/homepage`, {
          show_on_homepage: !!form.show_on_homepage,
          homepage_section: form.homepage_section || "Not Featured",
          homepage_order:
            form.homepage_order === "" || form.homepage_order == null
              ? null
              : parseInt(form.homepage_order, 10),
        });
      }
      cancel();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Listing removed");
    load();
  };

  return (
    <div data-testid="seller-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      {!editing && (
        <>
          <StripeWarningBanner
            payoutsEnabled={!!user?.stripe_payouts_enabled}
            onConnect={() => goToField("payouts", null)}
          />
          <SellerOnboarding
            user={user}
            productCount={products.length}
            onNavigate={goToField}
            onAddProduct={() => { startNew(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          />
        </>
      )}
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-3">◦ The Studio</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">{user?.business_name || user?.name}</h1>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="add-product-btn" className="btn-primary px-5 py-3 text-sm tracking-wide rounded-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New listing
          </button>
        )}
      </div>

      {!editing && (
        <div ref={tabsRef} className="border-b border-border/40 mb-8 flex gap-8">
          <button onClick={() => setTab("products")} className={`pb-3 text-sm tracking-wide ${tab === "products" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="tab-products">
            Listings ({products.length})
          </button>
          <button onClick={() => setTab("orders")} className={`pb-3 text-sm tracking-wide ${tab === "orders" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="tab-orders">
            Orders ({orders.length})
          </button>
          <button onClick={() => setTab("profile")} className={`pb-3 text-sm tracking-wide ${tab === "profile" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="tab-profile">
            Public profile
          </button>
          <button onClick={() => setTab("payouts")} className={`pb-3 text-sm tracking-wide ${tab === "payouts" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid="tab-payouts">
            Payouts
          </button>
          <Link to={`/shop/${user?.id}`} className="pb-3 text-sm tracking-wide text-muted-foreground hover:text-primary ml-auto">View public page →</Link>
        </div>
      )}

      {editing && (
        <form onSubmit={save} className="border border-border/40 p-8 rounded-sm">
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-serif text-2xl">{editing === "new" ? "New listing" : "Edit listing"}</h2>
            <button type="button" onClick={cancel} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Title</label>
              <input data-testid="product-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Description</label>
              <textarea data-testid="product-description" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors resize-none" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Price (USD)</label>
              <input data-testid="product-price" type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger
                  data-testid="product-category"
                  className="w-full bg-transparent border-0 border-b border-border rounded-none focus:ring-0 focus:border-primary px-0 py-3 mt-2 h-auto text-base transition-colors data-[placeholder]:text-muted-foreground"
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Stock</label>
              <input data-testid="product-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
            </div>
            {user?.role === "admin" && (
              <div className="md:col-span-2 border border-primary/30 bg-primary/5 rounded-sm p-5" data-testid="homepage-placement">
                <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">◦ Homepage placement (admin only)</div>
                <label className="flex items-center gap-3 cursor-pointer mb-5">
                  <input
                    data-testid="homepage-show-checkbox"
                    type="checkbox"
                    checked={!!form.show_on_homepage}
                    onChange={(e) => setForm({ ...form, show_on_homepage: e.target.checked })}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-sm">Show on homepage</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Homepage section</label>
                    <Select value={form.homepage_section || "Not Featured"} onValueChange={(v) => setForm({ ...form, homepage_section: v })}>
                      <SelectTrigger data-testid="homepage-section" className="w-full bg-transparent border-0 border-b border-border rounded-none focus:ring-0 focus:border-primary px-0 py-3 mt-2 h-auto text-base transition-colors">
                        <SelectValue placeholder="Not Featured" />
                      </SelectTrigger>
                      <SelectContent>
                        {["New Arrivals", "Best Sellers", "Featured Products", "Not Featured"].map((s) => (
                          <SelectItem key={s} value={s} data-testid={`homepage-section-option-${s}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Display order (optional)</label>
                    <input
                      data-testid="homepage-order"
                      type="number"
                      min="0"
                      value={form.homepage_order ?? ""}
                      onChange={(e) => setForm({ ...form, homepage_order: e.target.value })}
                      placeholder="e.g. 1"
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
                    />
                  </div>
                </div>
              </div>
            )}
            {/* Product Options / Variants (optional) */}
            <div className="md:col-span-2 border border-border rounded-sm p-5" data-testid="variants-section">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Product Options / Variants <span className="normal-case tracking-normal">(optional)</span></label>
                <button type="button" data-testid="add-option-btn" onClick={addOption} className="text-xs inline-flex items-center gap-1 text-primary hover:underline">
                  <Plus className="w-3.5 h-3.5" /> Add Option
                </button>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Add options like Scent, Size or Colour. Leave empty for a single-version product.</p>

              {(form.options || []).map((opt, i) => (
                <div key={i} className="border border-border/60 rounded-sm p-4 mb-3 bg-secondary/30" data-testid={`option-row-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      data-testid={`option-name-${i}`}
                      value={opt.name}
                      onChange={(e) => setOptName(i, e.target.value)}
                      placeholder="Option name (e.g. Scent)"
                      className="flex-1 bg-transparent border-b border-border focus:border-primary outline-none py-2 text-sm"
                    />
                    <button type="button" onClick={() => removeOption(i)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Remove option">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {opt.values.map((val) => (
                      <span key={val} className="inline-flex items-center gap-1 bg-card border border-border rounded-sm px-2.5 py-1 text-xs">
                        {val}
                        <button type="button" onClick={() => removeValue(i, val)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                    <input
                      data-testid={`option-value-input-${i}`}
                      value={valueDraft[i] || ""}
                      onChange={(e) => setValueDraft((d) => ({ ...d, [i]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addValue(i); } }}
                      placeholder="Add value + Enter"
                      className="bg-transparent border-b border-border focus:border-primary outline-none py-1 text-xs w-36"
                    />
                    <button type="button" data-testid={`add-value-btn-${i}`} onClick={() => addValue(i)} className="text-xs text-primary hover:underline">Add</button>
                  </div>
                </div>
              ))}

              {/* Variant matrix */}
              {form.variants && form.variants.length > 0 && (
                <div className="mt-4" data-testid="variant-matrix">
                  <div className="flex flex-wrap items-end gap-3 mb-3">
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Apply one price to all</label>
                      <input type="number" min="0" step="0.01" value={applyPrice} onChange={(e) => setApplyPrice(e.target.value)} placeholder="$" data-testid="apply-price-input"
                        className="block w-28 bg-transparent border-b border-border focus:border-primary outline-none py-1.5 text-sm mt-1" />
                    </div>
                    <button type="button" data-testid="apply-price-all-btn" onClick={applyPriceToAll} className="text-xs border border-border rounded-sm px-3 py-2 hover:bg-secondary">Apply to all</button>
                    <span className="text-xs text-muted-foreground">{form.variants.length} variants</span>
                  </div>
                  <div className="space-y-2">
                    {form.variants.map((v) => (
                      <div key={v.id} data-testid={`variant-row-${v.id}`} className="flex flex-wrap items-center gap-3 border border-border/60 rounded-sm p-3 bg-card">
                        <div className="w-full sm:w-40 text-sm font-medium">{v.id}</div>
                        <label className="w-14 h-14 border border-dashed border-border rounded-sm flex items-center justify-center cursor-pointer overflow-hidden flex-shrink-0 hover:border-primary" title="Variant image (optional)">
                          {v.image ? <img src={resolveImg(v.image)} alt="" className="w-full h-full object-cover" /> : <Upload className="w-4 h-4 text-muted-foreground" />}
                          <input type="file" accept="image/*" className="hidden" data-testid={`variant-image-${v.id}`} onChange={(e) => uploadVariantImage(v.id, e.target.files?.[0])} />
                        </label>
                        <div>
                          <label className="text-[10px] tracking-wider uppercase text-muted-foreground">Price</label>
                          <input type="number" min="0" step="0.01" value={v.price} data-testid={`variant-price-${v.id}`} onChange={(e) => updateVariant(v.id, { price: e.target.value })}
                            className="block w-24 bg-transparent border-b border-border focus:border-primary outline-none py-1 text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-wider uppercase text-muted-foreground">Qty</label>
                          <input type="number" min="0" value={v.stock} data-testid={`variant-stock-${v.id}`} onChange={(e) => updateVariant(v.id, { stock: e.target.value })}
                            className="block w-20 bg-transparent border-b border-border focus:border-primary outline-none py-1 text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-wider uppercase text-muted-foreground">SKU (optional)</label>
                          <input value={v.sku || ""} data-testid={`variant-sku-${v.id}`} onChange={(e) => updateVariant(v.id, { sku: e.target.value })}
                            className="block w-28 bg-transparent border-b border-border focus:border-primary outline-none py-1 text-sm" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Leave a variant image blank to use the product's main image. Product price shows as "from" the lowest variant price.</p>
                </div>
              )}
            </div>

            {/* Shipping (per listing — applies to all variants) */}
            <div className="md:col-span-2 border border-border rounded-sm p-5" data-testid="product-shipping-section">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-4 h-4 text-primary" />
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Shipping <span className="normal-case tracking-normal">(per listing)</span></label>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Rates apply to the whole listing and all its variants. Charged once per listing — you keep 100% of shipping.</p>
              <label className="flex items-center gap-3 text-sm cursor-pointer mb-4">
                <input
                  data-testid="product-free-shipping"
                  type="checkbox"
                  checked={!!form.free_shipping}
                  onChange={(e) => setForm({ ...form, free_shipping: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                Offer free shipping on this listing
              </label>
              {!form.free_shipping && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Canada (USD)</label>
                    <input
                      data-testid="product-shipping-canada"
                      type="number" step="0.01" min="0"
                      value={form.shipping_canada}
                      onChange={(e) => setForm({ ...form, shipping_canada: e.target.value })}
                      placeholder="e.g. 5.00"
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">United States (USD)</label>
                    <input
                      data-testid="product-shipping-us"
                      type="number" step="0.01" min="0"
                      value={form.shipping_us}
                      onChange={(e) => setForm({ ...form, shipping_us: e.target.value })}
                      placeholder="e.g. 8.00"
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">International (USD)</label>
                    <input
                      data-testid="product-shipping-international"
                      type="number" step="0.01" min="0"
                      value={form.shipping_international}
                      onChange={(e) => setForm({ ...form, shipping_international: e.target.value })}
                      placeholder="e.g. 20.00"
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
                    />
                  </div>
                </div>
              )}
              {!form.free_shipping && (
                <p className="text-[10px] text-muted-foreground mt-3">Leave a field blank to use your profile’s default shipping rate for that region.</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3 block">Images</label>
              <div className="flex flex-wrap gap-3">
                {form.images?.map((p, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-sm overflow-hidden border border-border">
                    <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-background/90 rounded-full p-1">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-24 h-24 border border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors rounded-sm">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-[10px] mt-1 text-muted-foreground">{uploading ? "Uploading…" : "Upload"}</span>
                  <input data-testid="image-upload-input" type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => uploadFiles(Array.from(e.target.files))} />
                </label>
              </div>
            </div>
          </div>
          <div className="mt-8 flex gap-3">
            <button data-testid="save-product-btn" className="btn-primary px-6 py-3 text-sm rounded-sm">Save listing</button>
            <button type="button" onClick={cancel} className="border border-border px-6 py-3 text-sm rounded-sm hover:bg-secondary transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {!editing && tab === "products" && (
        products.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p>No listings yet — open your first piece.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 border-collapse">
            {products.map((p) => (
              <div key={p.id} className="border border-border/40 rounded-sm overflow-hidden">
                <div className="aspect-square bg-secondary">
                  {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{p.category}</div>
                  <h3 className="font-serif text-lg mt-1 leading-tight">{p.title}</h3>
                  <div className="flex justify-between items-baseline mt-3">
                    <span className="text-sm">${Number(p.price).toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground">{p.stock} in stock</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => startEdit(p)} className="flex-1 text-xs border border-border py-2 hover:bg-secondary transition-colors rounded-sm inline-flex items-center justify-center gap-1">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => del(p.id)} className="text-xs border border-border p-2 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors rounded-sm">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {!editing && tab === "orders" && (
        orders.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">No orders yet.</div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => {
              const mine = o.items.filter((i) => i.seller_id === user.id);
              const subtotal = mine.reduce((s, i) => s + i.price * i.quantity, 0);
              return (
                <div key={o.id} className="border border-border/40 p-6 rounded-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Order #{o.id.slice(0, 8)}</div>
                      <div className="text-sm mt-1">{o.buyer_email}</div>
                      <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="font-serif text-xl">${subtotal.toFixed(2)}</div>
                  </div>
                  <div className="mt-4 text-sm text-muted-foreground">{mine.map((i) => `${i.quantity}× ${i.title}${i.variant_label ? ` (${i.variant_label})` : ""}`).join(" · ")}</div>
                  {o.shipping_address && (o.shipping_address.line1 || o.shipping_address.city) && (
                    <div className="mt-3 text-xs text-muted-foreground">
                      <span className="uppercase tracking-wider">Ship to:</span>{" "}
                      {[o.shipping_address.name, o.shipping_address.line1, o.shipping_address.line2, o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code, o.shipping_address.country].filter(Boolean).join(", ")}
                    </div>
                  )}
                  <SellerOrderFulfillment order={o} onSaved={load} />
                </div>
              );
            })}
          </div>
        )
      )}

      {!editing && tab === "profile" && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const instagram_posts = profile.instagram_posts_text
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean)
                .slice(0, 4);
              await api.put("/auth/me", {
                name: profile.name,
                bio: profile.bio,
                logo: profile.logo,
                banner: profile.banner,
                instagram: profile.instagram,
                business_name: profile.business_name,
                instagram_posts,
                shipping_domestic: Number(profile.shipping_domestic) || 0,
                shipping_international: Number(profile.shipping_international) || 0,
                ships_internationally: !!profile.ships_internationally,
                free_shipping_threshold: Number(profile.free_shipping_threshold) || 0,
              });
              await refresh();
              toast.success("Profile updated");
            } catch (err) {
              toast.error(formatApiErrorDetail(err.response?.data?.detail));
            }
          }}
          className="border border-border p-8 rounded-sm bg-card max-w-2xl"
        >
          <h2 className="font-serif text-2xl mb-2">Your public shop page</h2>
          <p className="text-sm text-muted-foreground mb-8">
            What buyers see at <Link to={`/shop/${user?.id}`} className="text-primary hover:underline">/shop/{user?.id?.slice(0, 8)}…</Link>
          </p>
          <div className="space-y-6">
            <div id="task-name">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Shop / Business name</label>
              <input
                data-testid="profile-business-name"
                value={profile.business_name}
                onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                placeholder="e.g. Maison Lumi"
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 text-base transition-colors"
              />
              <p className="text-xs text-muted-foreground mt-2">Shown on every product card and your public page. Updating renames all your listings instantly.</p>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Your name (private)</label>
              <input
                data-testid="profile-name"
                required
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="e.g. Léa Bernard"
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 text-base transition-colors"
              />
              <p className="text-xs text-muted-foreground mt-2">For our records and direct messages. Buyers see this only when you message them.</p>
            </div>
            <div id="task-desc">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Shop bio</label>
              <textarea
                data-testid="profile-bio"
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                rows={4}
                placeholder="Share your story. Where you create, what inspires you, what makes each piece special."
                className="w-full bg-transparent border border-border focus:border-primary outline-none p-3 mt-2 text-sm rounded-sm transition-colors resize-none"
              />
            </div>
            <div id="task-logo">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Shop logo</label>
              <div className="flex items-center gap-4 mt-2">
                {profile.logo ? (
                  <img src={resolveImg(profile.logo)} alt="Logo" data-testid="profile-logo-preview" className="w-20 h-20 rounded-full object-cover border border-border" />
                ) : (
                  <div className="w-20 h-20 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground"><Upload className="w-5 h-5" /></div>
                )}
                <div className="flex items-center gap-3">
                  <label data-testid="profile-logo-upload" className="border border-border px-4 py-2 text-sm rounded-sm cursor-pointer hover:bg-secondary transition-colors">
                    {uploadingProfile === "logo" ? "Uploading…" : (profile.logo ? "Replace" : "Upload logo")}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadProfileImage(e.target.files[0], "logo")} />
                  </label>
                  {profile.logo && <button type="button" data-testid="profile-logo-remove" onClick={() => setProfile({ ...profile, logo: "" })} className="text-sm text-muted-foreground hover:text-destructive transition-colors">Remove</button>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">A square image works best — shown as a circle on your shop page.</p>
            </div>
            <div id="task-banner">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Shop banner</label>
              {profile.banner ? (
                <img src={resolveImg(profile.banner)} alt="Banner" data-testid="profile-banner-preview" className="w-full h-36 object-cover rounded-sm border border-border mt-2" />
              ) : (
                <div className="w-full h-36 rounded-sm border border-dashed border-border flex items-center justify-center text-muted-foreground mt-2"><Upload className="w-5 h-5" /></div>
              )}
              <div className="flex items-center gap-3 mt-3">
                <label data-testid="profile-banner-upload" className="border border-border px-4 py-2 text-sm rounded-sm cursor-pointer hover:bg-secondary transition-colors">
                  {uploadingProfile === "banner" ? "Uploading…" : (profile.banner ? "Replace" : "Upload banner")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadProfileImage(e.target.files[0], "banner")} />
                </label>
                {profile.banner && <button type="button" data-testid="profile-banner-remove" onClick={() => setProfile({ ...profile, banner: "" })} className="text-sm text-muted-foreground hover:text-destructive transition-colors">Remove</button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">A wide image (≈1200×300) displayed across the top of your shop page.</p>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Instagram handle</label>
              <div className="flex items-center gap-2 border-b border-border focus-within:border-primary mt-2 transition-colors">
                <Instagram className="w-4 h-4 text-muted-foreground" />
                <input
                  data-testid="profile-instagram"
                  value={profile.instagram}
                  onChange={(e) => setProfile({ ...profile, instagram: e.target.value.replace(/^@/, "") })}
                  placeholder="yourhandle"
                  className="flex-1 bg-transparent outline-none py-3 text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">We’ll show this on your public page so buyers can follow you.</p>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Featured Instagram posts</label>
              <textarea
                data-testid="profile-instagram-posts"
                value={profile.instagram_posts_text}
                onChange={(e) => setProfile({ ...profile, instagram_posts_text: e.target.value })}
                rows={4}
                placeholder={"Paste up to 4 Instagram post URLs, one per line:\nhttps://www.instagram.com/p/Cxxxxxx/\nhttps://www.instagram.com/reel/Cxxxxxx/"}
                className="w-full bg-transparent border border-border focus:border-primary outline-none p-3 mt-2 text-sm rounded-sm transition-colors resize-none font-mono"
              />
              <p className="text-xs text-muted-foreground mt-2">These render as real Instagram embeds on your public shop page.</p>
            </div>

            {/* Shipping & delivery */}
            <div id="task-shipping" className="pt-6 mt-2 border-t border-border">
              <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-2">◦ Shipping &amp; delivery</div>
              <h3 className="font-serif text-xl mb-1">Your shipping rates</h3>
              <p className="text-xs text-muted-foreground mb-5">
                You keep 100% of shipping fees (Alteza’s 8% applies to product subtotal only).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Domestic rate (USD)</label>
                  <input
                    data-testid="profile-shipping-domestic"
                    type="number" step="0.01" min="0"
                    value={profile.shipping_domestic}
                    onChange={(e) => setProfile({ ...profile, shipping_domestic: e.target.value })}
                    placeholder="e.g. 8.00"
                    className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Leave blank or 0 for free domestic shipping.</p>
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Free shipping over (USD)</label>
                  <input
                    data-testid="profile-free-shipping-threshold"
                    type="number" step="0.01" min="0"
                    value={profile.free_shipping_threshold}
                    onChange={(e) => setProfile({ ...profile, free_shipping_threshold: e.target.value })}
                    placeholder="e.g. 75.00"
                    className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Optional threshold to unlock free shipping.</p>
                </div>
              </div>
              <div className="mt-5">
                <label className="flex items-center gap-3 text-sm">
                  <input
                    data-testid="profile-ships-intl"
                    type="checkbox"
                    checked={profile.ships_internationally}
                    onChange={(e) => setProfile({ ...profile, ships_internationally: e.target.checked })}
                    className="w-4 h-4"
                  />
                  I ship internationally
                </label>
                {profile.ships_internationally && (
                  <div className="mt-4 max-w-xs">
                    <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">International rate (USD)</label>
                    <input
                      data-testid="profile-shipping-intl"
                      type="number" step="0.01" min="0"
                      value={profile.shipping_international}
                      onChange={(e) => setProfile({ ...profile, shipping_international: e.target.value })}
                      placeholder="e.g. 25.00"
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <button data-testid="profile-save" className="btn-primary mt-8 px-6 py-3 text-sm rounded-sm">Save profile</button>
        </form>
      )}

      {!editing && tab === "payouts" && (
        <div className="max-w-2xl" data-testid="payouts-panel">
          <div className="border border-border p-8 rounded-sm bg-card">
            <div className="flex items-center gap-3 mb-2">
              <Banknote className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl">Get paid</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Connect a Stripe account to receive your earnings automatically. You keep
              <span className="text-foreground font-medium"> 92% of the product subtotal plus 100% of shipping</span>,
              minus Stripe’s payment processing fee (about 2.9% + $0.30 per order). Alteza’s 8% commission
              applies to the subtotal only. Stripe deposits your payouts straight to your bank.
            </p>

            {payout === null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking your payout account…
              </div>
            ) : !payout.connected ? (
              <div className="border-t border-border pt-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4" data-testid="payout-status-disconnected">
                  <AlertCircle className="w-4 h-4 text-amber-600" /> No payout account connected yet.
                </div>
                <div className="mb-5 max-w-sm">
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Your country</label>
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger
                      data-testid="stripe-country-select"
                      className="w-full bg-transparent border-0 border-b border-border rounded-none focus:ring-0 focus:border-primary px-0 py-3 mt-2 h-auto text-base transition-colors data-[placeholder]:text-muted-foreground"
                    >
                      <SelectValue placeholder={countries.length ? "Select your country" : "Loading countries…"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {countries.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{countryName(c)} ({c})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-2">Choose the country where your bank account is. This can’t be changed once your Stripe account is active.</p>
                </div>
                <button
                  onClick={() => connectStripe(selectedCountry)}
                  disabled={connecting || !selectedCountry}
                  data-testid="connect-stripe-btn"
                  className="btn-primary px-6 py-3 text-sm rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
                  Connect with Stripe
                </button>
                <p className="text-xs text-muted-foreground mt-3">
                  You’ll be taken to Stripe to add your bank and verify your identity. It takes a few minutes.
                </p>
              </div>
            ) : payout.payouts_enabled ? (
              <div className="border-t border-border pt-6">
                <div className="flex items-center gap-2 text-sm font-medium text-green-700 mb-2" data-testid="payout-status-active">
                  <CheckCircle2 className="w-5 h-5" /> Payouts are active
                </div>
                {payout.country && (
                  <p className="text-xs text-muted-foreground mb-2" data-testid="payout-country">Country: {countryName(payout.country)} ({payout.country})</p>
                )}
                <p className="text-sm text-muted-foreground mb-5">
                  You’re all set — earnings from new orders will be transferred to your bank automatically.
                </p>
                <button
                  onClick={() => connectStripe()}
                  disabled={connecting}
                  data-testid="manage-stripe-btn"
                  className="border border-border px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2 hover:bg-secondary transition-colors disabled:opacity-60"
                >
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  Manage on Stripe
                </button>
              </div>
            ) : (
              <div className="border-t border-border pt-6">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-700 mb-2" data-testid="payout-status-incomplete">
                  <AlertCircle className="w-5 h-5" /> Almost there — finish onboarding
                </div>
                {payout.country && (
                  <p className="text-xs text-muted-foreground mb-2" data-testid="payout-country">Country: {countryName(payout.country)} ({payout.country})</p>
                )}
                <p className="text-sm text-muted-foreground mb-5">
                  Your Stripe account isn’t fully verified yet, so payouts are paused. Complete the remaining steps to start receiving money.
                </p>
                <button
                  onClick={() => connectStripe()}
                  disabled={connecting}
                  data-testid="continue-stripe-btn"
                  className="btn-primary px-6 py-3 text-sm rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
                  Continue onboarding
                </button>
                <div className="mt-6 pt-5 border-t border-border/60">
                  {!changeCountry ? (
                    <button
                      onClick={() => { setChangeCountry(true); setSelectedCountry(""); }}
                      data-testid="wrong-country-btn"
                      className="text-xs text-muted-foreground hover:text-primary underline underline-offset-4"
                    >
                      Picked the wrong country? Start over →
                    </button>
                  ) : (
                    <div className="max-w-sm">
                      <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">New country</label>
                      <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                        <SelectTrigger
                          data-testid="stripe-country-select-change"
                          className="w-full bg-transparent border-0 border-b border-border rounded-none focus:ring-0 focus:border-primary px-0 py-3 mt-2 h-auto text-base transition-colors data-[placeholder]:text-muted-foreground"
                        >
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {countries.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`country-change-option-${c}`}>{countryName(c)} ({c})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground mt-2">This deletes your unverified Stripe account and starts fresh in the new country. Only works before your account is active.</p>
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => connectStripe(selectedCountry)}
                          disabled={connecting || !selectedCountry || selectedCountry === payout.country}
                          data-testid="switch-country-btn"
                          className="btn-primary px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
                        >
                          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
                          Switch country & restart
                        </button>
                        <button onClick={() => setChangeCountry(false)} className="border border-border px-4 py-2.5 text-sm rounded-sm hover:bg-secondary transition-colors">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
